import React, { useState, useEffect } from "react";
import { Modal, Form, Row, Col, Button, Spinner, Alert, Badge } from "react-bootstrap";
import { FaTimes, FaPlus } from "react-icons/fa";
import swal from "sweetalert";
import api from "../../../api";

const MomEmailModal = ({ show, onHide, mom, type = "mom", onSuccess }) => {
  const [toEmails, setToEmails] = useState([]);
  const [ccEmails, setCcEmails] = useState([]);
  const [bccEmails, setBccEmails] = useState([]);
  const [showCc, setShowCc] = useState(false);
  const [showBcc, setShowBcc] = useState(false);

  const [toInput, setToInput] = useState("");
  const [ccInput, setCcInput] = useState("");
  const [bccInput, setBccInput] = useState("");

  const [clientInfo, setClientInfo] = useState(null);
  const [clientContacts, setClientContacts] = useState([]);
  const [loadingContacts, setLoadingContacts] = useState(false);

  const [date, setDate] = useState(new Date().toISOString().split("T")[0]);
  const [time, setTime] = useState(
    new Date().toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })
  );

  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);

  const extractContactsFromClient = (clientObj) => {
    if (!clientObj) return [];
    const list = [];
    const seen = new Set();

    const pushEmail = (email, name, role) => {
      const clean = String(email || "").trim().toLowerCase();
      if (!clean || !clean.includes("@") || seen.has(clean)) return;
      seen.add(clean);
      list.push({
        email: clean,
        name: name || clientObj.name || clientObj.clientName || "Client",
        designation: role || "Contact",
        label: `${name ? `${name} ` : ""}${role ? `(${role})` : ""}`.trim() || clean,
      });
    };

    if (clientObj.gmail) {
      pushEmail(clientObj.gmail, clientObj.name || clientObj.clientName, "Primary");
    }
    if (clientObj.email) {
      pushEmail(clientObj.email, clientObj.name || clientObj.clientName, "Email");
    }
    if (clientObj.companyDetails?.companyEmail) {
      pushEmail(clientObj.companyDetails.companyEmail, clientObj.name || clientObj.clientName, "Company");
    }

    const contacts = Array.isArray(clientObj.contactPersons)
      ? clientObj.contactPersons
      : clientObj.contactPerson
      ? [clientObj.contactPerson]
      : [];

    contacts.forEach((cp) => {
      if (cp?.email) {
        pushEmail(cp.email, cp.name, cp.designation || "Contact Person");
      }
    });

    return list;
  };

  useEffect(() => {
    if (show && mom) {
      setToInput("");
      setCcInput("");
      setBccInput("");
      setCcEmails([]);
      setBccEmails([]);
      setShowCc(false);
      setShowBcc(false);

      setDate(new Date().toISOString().split("T")[0]);
      setTime(
        new Date().toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })
      );

      const title =
        type === "other-mom"
          ? mom.subject || mom.companyName || "Meeting"
          : mom.project?.projectName || "Meeting";

      const formattedDate = mom.meetingDate
        ? new Date(mom.meetingDate).toLocaleDateString("en-IN", {
            day: "2-digit",
            month: "short",
            year: "numeric",
          })
        : "";

      setSubject(
        `Minutes of Meeting: ${title}${mom.momNumber ? ` (${mom.momNumber})` : ""}${
          formattedDate ? ` - ${formattedDate}` : ""
        }`
      );
      setBody("");

      // Resolve client & contacts for this project / MOM
      const resolvedClientId =
        mom.client?._id ||
        (typeof mom.client === "string" ? mom.client : null) ||
        mom.project?.client?._id ||
        (typeof mom.project?.client === "string" ? mom.project?.client : null) ||
        mom.workOrder?.client?._id ||
        (typeof mom.workOrder?.client === "string" ? mom.workOrder?.client : null);

      const embeddedClient =
        mom.client && typeof mom.client === "object" && (mom.client.gmail || mom.client.contactPersons?.length)
          ? mom.client
          : mom.project?.client && typeof mom.project.client === "object" && (mom.project.client.gmail || mom.project.client.contactPersons?.length)
          ? mom.project.client
          : mom.workOrder?.client && typeof mom.workOrder.client === "object" && (mom.workOrder.client.gmail || mom.workOrder.client.contactPersons?.length)
          ? mom.workOrder.client
          : null;

      if (embeddedClient) {
        setClientInfo(embeddedClient);
        const contacts = extractContactsFromClient(embeddedClient);
        setClientContacts(contacts);
        if (contacts.length > 0) {
          setToEmails([contacts[0].email]);
        } else {
          setToEmails([]);
        }
      } else if (resolvedClientId) {
        setLoadingContacts(true);
        api
          .get(`/clients/${resolvedClientId}`)
          .then((res) => {
            if (res.data?.success && res.data.data) {
              setClientInfo(res.data.data);
              const contacts = extractContactsFromClient(res.data.data);
              setClientContacts(contacts);
              if (contacts.length > 0) {
                setToEmails([contacts[0].email]);
              }
            }
          })
          .catch((err) => {
            console.error("Failed to fetch client contacts for MOM:", err);
            // Fallback candidate
            const candidate =
              mom.client?.gmail ||
              mom.client?.email ||
              mom.project?.client?.gmail ||
              mom.project?.client?.email ||
              "";
            setToEmails(candidate ? [candidate.toLowerCase()] : []);
          })
          .finally(() => {
            setLoadingContacts(false);
          });
      } else {
        setClientInfo(null);
        setClientContacts([]);
        const candidate =
          mom.client?.gmail ||
          mom.client?.email ||
          mom.project?.client?.gmail ||
          mom.project?.client?.email ||
          "";
        setToEmails(candidate ? [candidate.toLowerCase()] : []);
      }
    }
  }, [show, mom, type]);

  // Helpers to add & remove email strings
  const addEmailsToList = (listSetter, currentList, rawInput, inputSetter) => {
    if (!rawInput.trim()) return;
    const parts = rawInput
      .split(/[,\s]+/)
      .map((s) => s.trim().toLowerCase())
      .filter((s) => s && s.includes("@"));

    if (parts.length > 0) {
      listSetter([...new Set([...currentList, ...parts])]);
    }
    inputSetter("");
  };

  const removeEmailFromList = (listSetter, currentList, target) => {
    listSetter(currentList.filter((e) => e.toLowerCase() !== target.toLowerCase()));
  };


  const handleSelectAllContacts = () => {
    const all = clientContacts.map((c) => c.email.toLowerCase());
    setToEmails([...new Set([...toEmails, ...all])]);
  };

  const handleClearToEmails = () => {
    setToEmails([]);
  };

  const handleSend = async (e) => {
    e.preventDefault();

    // Flush any pending text in inputs
    let finalTo = [...toEmails];
    if (toInput.trim()) {
      const extra = toInput
        .split(/[,\s]+/)
        .map((s) => s.trim().toLowerCase())
        .filter((s) => s && s.includes("@"));
      finalTo = [...new Set([...finalTo, ...extra])];
    }

    if (finalTo.length === 0) {
      swal("Warning", "Please select or enter at least one recipient email address", "warning");
      return;
    }

    let finalCc = [...ccEmails];
    if (showCc && ccInput.trim()) {
      const extra = ccInput
        .split(/[,\s]+/)
        .map((s) => s.trim().toLowerCase())
        .filter((s) => s && s.includes("@"));
      finalCc = [...new Set([...finalCc, ...extra])];
    }

    let finalBcc = [...bccEmails];
    if (showBcc && bccInput.trim()) {
      const extra = bccInput
        .split(/[,\s]+/)
        .map((s) => s.trim().toLowerCase())
        .filter((s) => s && s.includes("@"));
      finalBcc = [...new Set([...finalBcc, ...extra])];
    }

    if (!subject.trim()) {
      swal("Warning", "Please enter email subject", "warning");
      return;
    }

    setSending(true);
    try {
      const endpoint =
        type === "other-mom"
          ? `/other-mom/${mom._id}/send-email`
          : `/mom/${mom._id}/send-email`;

      const res = await api.post(endpoint, {
        to: finalTo.join(", "),
        cc: showCc && finalCc.length > 0 ? finalCc.join(", ") : undefined,
        bcc: showBcc && finalBcc.length > 0 ? finalBcc.join(", ") : undefined,
        date: date || undefined,
        time: time || undefined,
        subject: subject.trim(),
        body: body.trim() || undefined,
      });

      if (res.data?.success) {
        swal("Success", "Minutes of Meeting sent successfully via email!", "success");
        if (onSuccess) onSuccess();
        onHide();
      } else {
        swal("Error", res.data?.message || "Failed to send email", "error");
      }
    } catch (err) {
      console.error("Send MOM email error:", err);
      swal(
        "Error",
        err.response?.data?.message || "Failed to send email. Please try again.",
        "error"
      );
    } finally {
      setSending(false);
    }
  };

  const clientDisplayName =
    clientInfo?.name ||
    clientInfo?.clientName ||
    mom?.clientName ||
    mom?.project?.projectName ||
    "Client";

  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title className="fs-16 font-w600 text-primary">
          <i className="fas fa-envelope me-2"></i>
          Send {type === "other-mom" ? "Other MOM" : "Minutes of Meeting (MOM)"} via Email
        </Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSend}>
        <Modal.Body className="py-3 px-4">
          <Alert variant="info" className="d-flex align-items-center py-2 px-3 mb-3 fs-13">
            <i className="fas fa-paperclip me-2 fs-15 text-primary"></i>
            <div>
              <strong>Official MOM PDF</strong> will be automatically generated and attached with this email.
            </div>
          </Alert>

          {loadingContacts && (
            <div className="text-muted fs-12 mb-2 d-flex align-items-center gap-2">
              <Spinner animation="border" size="sm" />
              Loading saved project client contacts...
            </div>
          )}

          {/* 🔹 TO RECIPIENTS */}
          <Form.Group className="mb-3">
            <div className="d-flex justify-content-between align-items-center mb-1">
              <Form.Label className="fs-13 font-w600 text-dark mb-0">
                Recipient Emails (To) <span className="text-danger">*</span>
              </Form.Label>
              <div className="d-flex gap-2">
                {!showCc && (
                  <Button
                    variant="link"
                    size="sm"
                    className="p-0 text-decoration-none fs-12"
                    onClick={() => setShowCc(true)}
                  >
                    + CC
                  </Button>
                )}
                {!showBcc && (
                  <Button
                    variant="link"
                    size="sm"
                    className="p-0 text-decoration-none fs-12"
                    onClick={() => setShowBcc(true)}
                  >
                    + BCC
                  </Button>
                )}
              </div>
            </div>

            {/* Selected Email Tags Display */}
            {toEmails.length > 0 && (
              <div
                className="d-flex flex-wrap gap-1 p-2 mb-2 border rounded"
                style={{ background: "#ffffff", minHeight: "38px" }}
              >
                {toEmails.map((email, idx) => (
                  <Badge
                    key={idx}
                    bg="primary"
                    className="d-flex align-items-center gap-1 px-2 py-1 fs-12 font-w500"
                  >
                    <span>{email}</span>
                    <FaTimes
                      style={{ cursor: "pointer", fontSize: "10px" }}
                      onClick={() => removeEmailFromList(setToEmails, toEmails, email)}
                      title="Remove"
                    />
                  </Badge>
                ))}
                <Button
                  variant="link"
                  size="sm"
                  className="p-0 ms-auto text-danger text-decoration-none fs-11 align-self-center"
                  onClick={handleClearToEmails}
                >
                  Clear All
                </Button>
              </div>
            )}

            {/* Dropdown for Saved Client Contacts + Additional Email Input */}
            <Row className="g-2">
              {clientContacts.length > 0 && (
                <Col md={6}>
                  <Form.Select
                    value=""
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val === "__ALL__") {
                        handleSelectAllContacts();
                      } else if (val && !toEmails.includes(val.toLowerCase())) {
                        setToEmails([...toEmails, val.toLowerCase()]);
                      }
                    }}
                    disabled={sending}
                  >
                    <option value="">
                      -- Select Saved Client Contact ({clientDisplayName}) --
                    </option>
                    <option value="__ALL__" className="fw-bold text-primary">
                      ✓ Select All Contacts ({clientContacts.length})
                    </option>
                    {clientContacts.map((c, idx) => {
                      const isSelected = toEmails.some(
                        (e) => e.toLowerCase() === c.email.toLowerCase()
                      );
                      return (
                        <option key={idx} value={c.email}>
                          {isSelected ? "✓ " : ""}{c.name} {c.designation ? `(${c.designation})` : ""} - {c.email}
                        </option>
                      );
                    })}
                  </Form.Select>
                </Col>
              )}

              <Col md={clientContacts.length > 0 ? 6 : 12}>
                <div className="d-flex gap-1">
                  <Form.Control
                    type="text"
                    placeholder="Type additional email & press Enter..."
                    value={toInput}
                    onChange={(e) => setToInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === ",") {
                        e.preventDefault();
                        addEmailsToList(setToEmails, toEmails, toInput, setToInput);
                      }
                    }}
                    onBlur={() => addEmailsToList(setToEmails, toEmails, toInput, setToInput)}
                    disabled={sending}
                  />
                  <Button
                    variant="outline-primary"
                    onClick={() => addEmailsToList(setToEmails, toEmails, toInput, setToInput)}
                    disabled={sending || !toInput.trim()}
                    title="Add email"
                  >
                    <FaPlus />
                  </Button>
                </div>
              </Col>
            </Row>

            <Form.Text className="text-muted fs-11">
              Select saved contacts from the dropdown or type any additional email and press Enter / comma.
            </Form.Text>
          </Form.Group>

          {/* 🔹 CC */}
          {showCc && (
            <Form.Group className="mb-3">
              <div className="d-flex justify-content-between align-items-center mb-1">
                <Form.Label className="fs-13 font-w600 text-dark mb-0">CC</Form.Label>
                <Button
                  variant="link"
                  size="sm"
                  className="p-0 text-danger text-decoration-none fs-12"
                  onClick={() => {
                    setShowCc(false);
                    setCcEmails([]);
                    setCcInput("");
                  }}
                >
                  Remove CC
                </Button>
              </div>

              {ccEmails.length > 0 && (
                <div
                  className="d-flex flex-wrap gap-1 p-2 mb-2 border rounded"
                  style={{ background: "#ffffff", minHeight: "38px" }}
                >
                  {ccEmails.map((email, idx) => (
                    <Badge
                      key={idx}
                      bg="secondary"
                      className="d-flex align-items-center gap-1 px-2 py-1 fs-12 font-w500"
                    >
                      <span>{email}</span>
                      <FaTimes
                        style={{ cursor: "pointer", fontSize: "10px" }}
                        onClick={() => removeEmailFromList(setCcEmails, ccEmails, email)}
                        title="Remove"
                      />
                    </Badge>
                  ))}
                </div>
              )}

              <Row className="g-2">
                {clientContacts.length > 0 && (
                  <Col md={6}>
                    <Form.Select
                      value=""
                      onChange={(e) => {
                        const val = e.target.value;
                        if (val && !ccEmails.includes(val.toLowerCase())) {
                          setCcEmails([...ccEmails, val.toLowerCase()]);
                        }
                      }}
                      disabled={sending}
                    >
                      <option value="">-- Select Saved Contact for CC --</option>
                      {clientContacts.map((c, idx) => (
                        <option key={idx} value={c.email}>
                          {c.name} {c.designation ? `(${c.designation})` : ""} - {c.email}
                        </option>
                      ))}
                    </Form.Select>
                  </Col>
                )}
                <Col md={clientContacts.length > 0 ? 6 : 12}>
                  <div className="d-flex gap-1">
                    <Form.Control
                      type="text"
                      placeholder="Type CC email and press Enter..."
                      value={ccInput}
                      onChange={(e) => setCcInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === ",") {
                          e.preventDefault();
                          addEmailsToList(setCcEmails, ccEmails, ccInput, setCcInput);
                        }
                      }}
                      onBlur={() => addEmailsToList(setCcEmails, ccEmails, ccInput, setCcInput)}
                      disabled={sending}
                    />
                    <Button
                      variant="outline-secondary"
                      onClick={() => addEmailsToList(setCcEmails, ccEmails, ccInput, setCcInput)}
                      disabled={sending || !ccInput.trim()}
                      title="Add CC"
                    >
                      <FaPlus />
                    </Button>
                  </div>
                </Col>
              </Row>
            </Form.Group>
          )}

          {/* 🔹 BCC */}
          {showBcc && (
            <Form.Group className="mb-3">
              <div className="d-flex justify-content-between align-items-center mb-1">
                <Form.Label className="fs-13 font-w600 text-dark mb-0">BCC</Form.Label>
                <Button
                  variant="link"
                  size="sm"
                  className="p-0 text-danger text-decoration-none fs-12"
                  onClick={() => {
                    setShowBcc(false);
                    setBccEmails([]);
                    setBccInput("");
                  }}
                >
                  Remove BCC
                </Button>
              </div>

              {bccEmails.length > 0 && (
                <div
                  className="d-flex flex-wrap gap-1 p-2 mb-2 border rounded"
                  style={{ background: "#ffffff", minHeight: "38px" }}
                >
                  {bccEmails.map((email, idx) => (
                    <Badge
                      key={idx}
                      bg="dark"
                      className="d-flex align-items-center gap-1 px-2 py-1 fs-12 font-w500"
                    >
                      <span>{email}</span>
                      <FaTimes
                        style={{ cursor: "pointer", fontSize: "10px" }}
                        onClick={() => removeEmailFromList(setBccEmails, bccEmails, email)}
                        title="Remove"
                      />
                    </Badge>
                  ))}
                </div>
              )}

              <div className="d-flex gap-2">
                <Form.Control
                  type="text"
                  placeholder="Type BCC email and press Enter or comma..."
                  value={bccInput}
                  onChange={(e) => setBccInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === ",") {
                      e.preventDefault();
                      addEmailsToList(setBccEmails, bccEmails, bccInput, setBccInput);
                    }
                  }}
                  onBlur={() => addEmailsToList(setBccEmails, bccEmails, bccInput, setBccInput)}
                  disabled={sending}
                />
                <Button
                  variant="outline-dark"
                  onClick={() => addEmailsToList(setBccEmails, bccEmails, bccInput, setBccInput)}
                  disabled={sending || !bccInput.trim()}
                  title="Add BCC"
                >
                  <FaPlus className="me-1" /> Add
                </Button>
              </div>
            </Form.Group>
          )}

          {/* DATE & TIME */}
          <Row className="mb-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label className="fs-13 font-w600 text-dark">Date</Form.Label>
                <Form.Control
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  disabled={sending}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label className="fs-13 font-w600 text-dark">Time</Form.Label>
                <Form.Control
                  type="time"
                  value={time}
                  onChange={(e) => setTime(e.target.value)}
                  disabled={sending}
                />
              </Form.Group>
            </Col>
          </Row>

          {/* SUBJECT */}
          <Form.Group className="mb-3">
            <Form.Label className="fs-13 font-w600 text-dark">
              Subject <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              disabled={sending}
              required
            />
          </Form.Group>

          {/* EMAIL BODY (HTML SUPPORTED) */}
          <Form.Group className="mb-3">
            <Form.Label className="fs-13 font-w600 text-dark">
              Email Body (Optional / Custom Notes)
            </Form.Label>
            {body && (
              <div
                className="border rounded p-3 mb-2"
                style={{
                  minHeight: "100px",
                  maxHeight: "220px",
                  overflowY: "auto",
                  fontSize: "14px",
                  background: "#fff",
                }}
                dangerouslySetInnerHTML={{ __html: body }}
              />
            )}
            <Form.Control
              as="textarea"
              rows={3}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Add any additional notes for this email..."
              disabled={sending}
            />
          </Form.Group>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" size="sm" onClick={onHide} disabled={sending}>
            Cancel
          </Button>
          <Button variant="primary" size="sm" type="submit" disabled={sending}>
            {sending ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="me-2"
                />
                Sending Mail...
              </>
            ) : (
              <>
                <i className="fas fa-paper-plane me-2"></i>
                Send Mail
              </>
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default MomEmailModal;
